<template>
  <div class="shop-form">
    <div
      class="phase"
      v-if="currentShopStep <= 8 && (receiver === 'self' || receiver === null)"
    >
      <div class="phasetop">
        <span class="step"
          >{{ currentShopStep >= 6 ? currentShopStep - 1 : currentShopStep }}/{{
            $store.state.numberOfSteps["self"]
          }}</span
        >
        <span class="phasename" v-html="currentStepName"></span>
      </div>
      <div class="load">
        <div
          :style="{
            width:
              (100 / $store.state.numberOfSteps['self']) * currentShopStep +
              '%',
          }"
        ></div>
      </div>
    </div>
    <div class="phase" v-else-if="currentShopStep <= 8 && receiver === 'other'">
      <div class="phasetop">
        <span class="step"
          >{{ currentShopStep }}/{{ $store.state.numberOfSteps["other"] }}</span
        >
        <span class="phasename" v-html="currentStepName"></span>
      </div>
      <div class="load">
        <div
          :style="{
            width:
              (100 / $store.state.numberOfSteps['other']) * currentShopStep +
              '%',
          }"
        ></div>
      </div>
    </div>

    <stepSetReceiver v-if="currentShopStep === 1"></stepSetReceiver>

    <stepSetLoyality v-if="currentShopStep === 2"></stepSetLoyality>

    <stepSetAdditionalParams
      v-if="currentShopStep === 3"
    ></stepSetAdditionalParams>

    <stepSetDesign v-if="currentShopStep === 4"></stepSetDesign>

    <stepForm
      ref="stepClientDataForm"
      v-show="currentShopStep === 5"
    ></stepForm>

    <stepBuyerForm ref="stepBuyerDataForm" v-show="currentShopStep === 6">
    </stepBuyerForm>

    <stepAuth ref="stepAuthForm" v-show="currentShopStep === 7"></stepAuth>

    <stepFinish ref="stepFinishFrom" v-if="currentShopStep === 8"></stepFinish>

    <div
      class="expiration-date"
      v-if="[3].includes(currentShopStep) && currentBuyLoyalityType"
    >
      <span class="time">{{ currentBuyLoyality.expiration }}</span
      ><span class="price"
        >{{
          currentLoyalityKind === "certificate"
            ? currentBuyLoyality.type.balance.toLocaleString()
            : currentBuyLoyality.type.cost.toLocaleString()
        }}
        ₽</span
      >
    </div>

    <div
      class="shop-form__footer"
      :class="
        store.state.isPaymentLoader ? '_loader _loader-40px _loader-left' : ''
      "
      v-if="currentShopStep <= 8 && !store.state.currentPaymentData.OrderId"
    >
      <button
        class="button back"
        v-if="currentShopStep > 0 && !store.state.currentPaymentData.OrderId"
        @click="prevStep"
      >
        Назад
      </button>
      <button
        class="button next"
        v-if="currentShopStep < 7"
        @click="nextStep"
        :disabled="disableNextBtn"
      >
        {{
          currentShopStep === 5 && receiver === "self" && !$store.getters.isAuth
            ? "Подтвердить"
            : "Продолжить"
        }}
      </button>
      <button
        class="button next"
        v-else-if="currentShopStep === 8"
        @click="createPayment"
      >
        Перейти к оплате
      </button>
    </div>

    <a
      v-else-if="currentShopStep === 8 && !store.state.isPaymentSuccess"
      class="shop-form__footer button next"
      :href="currentPaymentUrl"
      target="_parent"
    >
      Перейти к оплате
    </a>

    <div class="underline" v-if="currentShopStep === 8">
      Нажимая кнопку «Перейти к оплате», вы принимаете
      <a class="link" href="https://tekstura.moscow/page/oferta/">оферту</a> и
      предоставляете согласие на
      <a
        class="link"
        target="_blank"
        href="https://tekstura.moscow/page/confidence/"
        >обработку персональных данных</a
      >
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, VueElement, watch } from "vue";
import { useStore } from "vuex";
import getExpirationStringForCertificate from "@/functions/getExpirationStringForCertificate";
import getExpirationStringForAbonement from "@/functions/getExpirationStringForAbonement";

import stepSetReceiver from "@/components/steps/stepSetReceiver.vue";
import stepSetLoyality from "@/components/steps/stepSetLoyality.vue";
import stepSetAdditionalParams from "@/components/steps/stepSetAdditionalParams.vue";
import stepSetDesign from "@/components/steps/stepSetDesign.vue";
import stepForm from "@/components/steps/stepForm.vue";
import stepBuyerForm from "@/components/steps/stepBuyerForm.vue";
import stepAuth from "./steps/stepAuth.vue";
import stepFinish from "./steps/stepFinish.vue";
import {
  IloyalityShopData,
  TypeReceiver,
  TypeSortedLoyalityByName,
  IbyCertificate,
  IbyAbonement,
} from "@/types/typesOfState";
import { TypeLoyalityKinds, TypeLoyaliyMainName } from "@/types";

const store = useStore();

const currentLoyalityKind = computed(() => store.state.currentLoyalityKind);
const currentShopStep = computed(() => store.state.currentShopStep);
const loyalityShopData = computed(() => store.state.loyalityShopData);
const loyalityTypes = computed(() => store.state.allLoyalityTypes);
const currentBuyLoyalityType = computed(
  () => store.state.loyalityShopData.buyLoyalityType
);

const receiver = computed<TypeReceiver>(
  () => store.state.loyalityShopData.receiver
);

const isNominal = computed(() => store.state.loyalityShopData.isNominal);

const currentBuyLoyality = computed(() => {
  const currentBuy = [
    ...loyalityTypes.value.certificates,
    ...loyalityTypes.value.abonements,
  ].find((i) => i.id === currentBuyLoyalityType.value);

  const expiration =
    currentLoyalityKind.value === "certificate"
      ? getExpirationStringForCertificate(currentBuy)
      : getExpirationStringForAbonement(currentBuy);

  return {
    type: currentBuy,
    expiration: expiration,
  };
});

const currentCertificateData = computed<IbyCertificate>(
  () => store.state.loyalityShopData.byCertificate
);
const currentSortedItemByCertificate = computed<TypeLoyaliyMainName>(
  () =>
    store.state.sortedLoyalityByName.certificates[
      currentCertificateData.value.name || ""
    ]
);
const currentAbonementData = computed<IbyAbonement>(
  () => store.state.loyalityShopData.byAbonement
);
const currentSortedItemByAbonement = computed<TypeLoyaliyMainName>(
  () =>
    store.state.sortedLoyalityByName.abonements[
      currentAbonementData.value.name || ""
    ]
);

const disableNextBtn = computed<boolean>(() => {
  switch (store.state.currentShopStep) {
    case 1:
      if (receiver.value !== null) {
        return false;
      }
      break;
    case 2:
      if (currentLoyalityKind.value === "certificate") {
        if (certificateData.value.name) {
          return false;
        }
      } else if (abonementData.value.name) {
        return false;
      }

      break;
    case 3:
      if (loyalityShopData.value.buyLoyalityType) {
        return false;
      }
      break;
    case 4:
      if (loyalityShopData.value.designType) {
        return false;
      }
      break;
  }
  if (store.state.currentShopStep > 4) {
    return false;
  }
  return true;
});

const currentStepName = computed(() => {
  const isCert: boolean = currentLoyalityKind.value === "certificate";

  let name: string | null | undefined = "";

  switch (store.state.currentShopStep) {
    case 1:
      name = isCert
        ? "Для кого приобретаете сертификат?"
        : "Для кого приобретаете абонемент?";
      break;
    case 2:
      name = isCert ? "Выберите сертификат" : "Выберите абонемент";
      break;
    case 3:
      if (isCert) {
        name =
          currentSortedItemByCertificate.value.title === "Номинал"
            ? "Укажите номинал"
            : "Выберите вариант";
      } else {
        name = "Выберите вариант";
      }
      break;
    case 4:
      name = isCert
        ? "Выберите дизайн сертификата"
        : "Выберите дизайн абонемента";
      break;
    case 5:
      name = "Данные обладателя";
      break;
    case 6:
      name = "Данные покупателя";
      break;
    case 7:
      name = "Подтверждение номера телефона";
      break;
    case 8:
      name = "проверьте данные перед оплатой";
      break;
  }

  return name;
});

const abonementData = computed(() => store.state.loyalityShopData.byAbonement);
const certificateData = computed(
  () => store.state.loyalityShopData.byCertificate
);

const stepClientDataForm = ref<VueElement | null>(null);
const stepBuyerDataForm = ref<VueElement | null>(null);
const stepAuthForm = ref<VueElement | null>(null);
const stepFinishFrom = ref<VueElement | null>(null);

const lastBlockStep = computed<number>(() => store.state.lastBlockStep);

const currentPaymentUrl = computed(
  () => store.state.currentPaymentData?.PaymentURL
);

function nextStep(): void {
  switch (store.state.currentShopStep) {
    case 5:
      if (stepClientDataForm.value.validateClientData()) {
        if (receiver.value === "self") {
          store.state.authModule.authPhone =
            store.state.loyalityShopData.clientData.phone;

          if (!store.state.authModule.tokens) {
            stepAuthForm.value.nextAuthStep(false);
            store.state.currentShopStep += 2;
          } else {
            store.state.currentShopStep += 3;
          }
        } else {
          store.state.currentShopStep++;
        }
      }
      break;
    case 6:
      if (stepBuyerDataForm.value.validateBuyerData()) {
        if (receiver.value === "other") {
          store.state.authModule.authPhone =
            store.state.loyalityShopData.buyerData.phone;

          if (!store.state.authModule.tokens) {
            stepAuthForm.value.nextAuthStep(false);
            store.state.currentShopStep++;
          } else {
            store.state.currentShopStep += 2;
          }
        } else {
          store.state.currentShopStep++;
        }
      }

      break;
    default:
      store.state.currentShopStep++;
  }
}

function prevStep(): void {
  switch (store.state.currentShopStep) {
    case 8:
      if (store.state.authModule.tokens) {
        store.state.currentShopStep -= 3;
      }
      break;
    case 7:
      if (receiver.value === "self") {
        store.state.currentShopStep -= 2;
        store.state.lastBlockStep = 0;
      } else {
        store.state.currentShopStep--;
        store.state.lastBlockStep = 0;
      }
      break;
    case 6:
      if (lastBlockStep.value === 1) {
        store.state.lastBlockStep--;
      }
      if (lastBlockStep.value === 0 || receiver.value === "self") {
        store.state.currentShopStep--;
      }
      break;
    case 4:
      store.state.loyalityShopData.designType = null;
      store.state.currentShopStep--;
      break;
    case 3:
      store.state.loyalityShopData.byAbonement.seanceLength = null;
      store.state.loyalityShopData.byAbonement.seats = null;
      store.state.loyalityShopData.byAbonement.name = null;

      store.state.loyalityShopData.byCertificate.name = null;
      store.state.loyalityShopData.byCertificate.nominal = null;
      store.state.loyalityShopData.byCertificate.seanceLength = null;

      store.state.loyalityShopData.buyLoyalityType = null;

      store.state.currentShopStep--;

      break;
    case 1:
      store.state.currentLoyalityKind = null;
      store.state.currentShopStep--;
      break;
    default:
      store.state.currentShopStep--;
  }
}

function createPayment(): void {
  if (stepFinishFrom.value.validateFinishForm()) {
    store.dispatch("buyLoyality");
  }
}
</script>

<style lang="scss">
@import "@/assets/variables.scss";

.link {
  color: #3696e0;
}

.phase {
  width: 100%;
  border: 1px solid #4b433c;
  border-radius: 0 0 16px 16px;
  padding: 16px;
  text-transform: uppercase;
}
.phasetop {
  display: flex;
  align-items: center;
  gap: 8px;
}
.step {
  font-size: 20px;
  font-weight: 400;
}
.load {
  position: relative;
  margin-top: 8px;
  background-color: $background-color_2;
  width: 100%;
  height: 8px;
  border-radius: 12px;
  overflow: hidden;
  > * {
    position: absolute;
    background-color: $background-color_3;
    width: 20%;
    height: 100%;
    top: 0;
    left: 0;
    border-radius: 12px 0 0 12px;
  }
}

.shop-form {
  display: flex;
  flex-direction: column;
  height: 100%;
  &__block {
    display: grid;
    grid-gap: 16px;
    display: grid;
    grid-gap: 16px;
    grid-template-rows: max-content;

    padding: 16px;
    border: 1px solid #4b433c;
    border-radius: 16px;
    margin: 16px 0 0 0;
  }
  @media (max-width: 1024px) {
    &__block._form .inputwrapper {
      margin-bottom: 0;
    }
  }
  &__head {
    font-size: 20px;
    align-self: flex-end;

    @media (max-width: 1023px) {
      font-size: 14px;
      text-align: center;
    }
  }
  &__footer {
    display: grid;
    grid-template-columns: 115px auto;
    grid-gap: 16px;
    margin-top: auto;
    padding-top: 32px;
    justify-self: end;
    margin-top: 20px;
    padding: 10px 0;

    position: sticky;
    bottom: -2px;
    left: 0;
    background: $color_2;
  }
  &__table {
    display: grid;
    grid-template-columns: 1fr 1fr;

    grid-gap: 8px;

    .button {
      font-size: 14px;
    }

    &._designs {
      display: grid;
      grid-gap: 8px;
      grid-template-columns: 1fr 1fr;
      padding-right: 7px;

      @media (min-width: 1024px) {
        max-height: 580px;
        overflow: auto;
      }
    }
  }
  &__table > *:only-child {
    grid-column: span 2;
  }
  &__title {
    font-size: 20px;
    font-weight: 400;
    color: #dadace;
  }
  &__design {
    cursor: pointer;
    display: block;
    width: 100%;
    height: 0;
    padding-top: 42%;
    background: no-repeat center;
    background-size: cover;
    background-color: #999;
    border: 4px solid transparent;
    border-radius: 12px;
    min-height: 274px;
  }
  &__design.active {
    border-color: #ece6dc;
  }
  &__inputs {
    display: grid;
    grid-gap: 8px;

    .soglasie {
      margin-top: 8px;
    }
  }
}
.quantity {
  display: grid;
  grid-template-columns: auto 66px 66px;
  align-items: center;
  grid-gap: 16px;
  margin: 16px 0;
  border: 1px solid #4b433c;
  border-radius: 16px;
  padding: 16px;
}
.expiration-date {
  border: 1px solid #4b433c;
  border-radius: 16px;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 300;
  margin-top: 49px;
}
.price {
  font-size: 20px;
  font-weight: 400;
}
.underline {
  margin-top: 16px;
  font-size: 14px;
  font-weight: 400;
  display: block;
}

.radio-label {
  padding-left: 24px;
}

@media (min-width: 1024px) {
  .phase {
    border-radius: 16px;
    padding: 12px 16px;
    grid-area: phase;
  }
}
@media (max-width: 1024px) {
  .phase {
    margin-top: -7px;
  }
}

@media (max-width: 768px) {
  .shop-form__table._designs {
    grid-template-columns: 1fr !important;
  }
}
@media (max-width: 460px) {
  .errorblock {
    left: 20%;
    font-size: 14px;
  }
}
</style>
